import { Opportunity } from "@/opportunity/opportunity";

/**
 * Configuration for failure pattern-specific charts
 * Each failure pattern can define what charts to display
 */

export type ChartType = 'opportunity_athena_card_general' | 'inlet_outlet_power' | 'custom';

export interface ChartConfig {
  type: ChartType;
  componentName: string; // Name of the component to load
  props?: Record<string, any>;
}

export interface FailurePatternChartConfig {
  failurePatternId: string;
  charts: ChartConfig[];
  // Optional: custom logic to determine if charts should be shown
  shouldShow?: (opportunity: Opportunity) => boolean;
}

/**
 * Configuration map for failure patterns
 * Add new failure patterns here with their chart configurations
 * 
 * To add a new failure pattern:
 * 1. Create your chart component (e.g., OpportunityCustomChart.tsx)
 * 2. Add an entry here with the failure pattern ID and component name
 * 3. The OpportunityFailurePatternCharts component will automatically render it
 */
export const FAILURE_PATTERN_CHART_CONFIGS: FailurePatternChartConfig[] = [
  {
    failurePatternId: 'refrigerant_undercharged__undercharged',
    charts: [
      {
        type: 'opportunity_athena_card_general',
        componentName: 'OpportunityCompressorTempCard',
      },
      {
        type: 'custom',
        componentName: 'FrequencySeverityOverTimeCard',
      },
    ],
  },
  {
    failurePatternId: 'suction_temp_less_than_freezing',
    charts: [
      {
        type: 'opportunity_athena_card_general',
        componentName: 'OpportunityFreezingSuctionCard',
      },
      {
        type: 'custom',
        componentName: 'FrequencySeverityOverTimeCard',
      },
    ],
  },
  {
    failurePatternId: 'faulty_operation_short_cycling',
    charts: [
      {
        type: 'custom',
        componentName: 'OpportunityShortCyclingCard',
      },
    ],
  },
  {
    failurePatternId: 'faulty_operation_short_cycling_duration',
    charts: [
      {
        type: 'custom',
        componentName: 'OpportunityShortCyclingCard',
      },
    ],
  },
  // Example: Add more failure patterns here:
  // {
  //   failurePatternId: 'another_failure_pattern',
  //   charts: [
  //     {
  //       type: 'custom',
  //       componentName: 'OpportunityCustomChart',
  //       props: { customProp: 'value' }
  //     },
  //   ],
  // },
];

/**
 * Get chart configuration for a specific failure pattern
 */
export const getFailurePatternChartConfig = (
  failurePatternId: string | undefined
): FailurePatternChartConfig | undefined => {
  if (!failurePatternId) return undefined;
  return FAILURE_PATTERN_CHART_CONFIGS.find(
    (config) => config.failurePatternId === failurePatternId
  );
};

/**
 * Check if an opportunity should show failure pattern charts
 */
export const shouldShowFailurePatternCharts = (opportunity: Opportunity): boolean => {
  const failurePatternId = (opportunity.category_object as any)?.detector_key;
  const config = getFailurePatternChartConfig(failurePatternId);
  
  if (!config) return false;
  
  // Use custom shouldShow logic if provided, otherwise default to true
  if (config.shouldShow) {
    return config.shouldShow(opportunity);
  }
  
  return true;
};

